// src/products/dataroom/components/SetupWizard/DataroomSetupWizard.js
import React, { useState } from 'react';
import { CheckCircle, ArrowLeft, Building2, Users, UserPlus, FileText } from 'lucide-react';
import PartySetupStep from './PartySetupStep';
import TeamSetupStep from './TeamSetupStep';
import UserInvitationStep from './UserInvitationStep';
import SummaryStep from './SummaryStep';

const DataroomSetupWizard = ({ projectId, projectName, onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [setupData, setSetupData] = useState({
    parties: [],
    teams: {},
    users: {}
  });

  const steps = [
    {
      id: 1,
      title: "Setup Parties",
      description: "Which parties will have access to this dataroom?",
      icon: Building2,
      component: PartySetupStep
    },
    {
      id: 2,
      title: "Configure Teams",
      description: "Set up teams and permissions for each party",
      icon: Users,
      component: TeamSetupStep
    },
    {
      id: 3,
      title: "Invite Users",
      description: "Add users to parties and teams",
      icon: UserPlus,
      component: UserInvitationStep
    },
    {
      id: 4,
      title: "Summary",
      description: "Review your dataroom setup",
      icon: FileText,
      component: SummaryStep
    }
  ];

  const handleStepComplete = (stepId, data) => {
    const updatedData = {
      ...setupData,
      ...data
    };
    
    setSetupData(updatedData);
    
    if (stepId < steps.length) {
      setCurrentStep(stepId + 1);
    } else {
      // Final completion
      onComplete(updatedData);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Setup Dataroom Access</h2>
              <p className="text-blue-100 text-sm mt-1">
                Configure parties, teams, and users for "{projectName}"
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="text-white hover:text-blue-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    isCompleted 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : isActive 
                        ? 'bg-blue-600 border-blue-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-400'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      Step {step.id}
                    </p>
                    <p className={`text-xs ${
                      isActive ? 'text-blue-700' : isCompleted ? 'text-green-700' : 'text-gray-400'
                    }`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-px mx-4 ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Step Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {steps[currentStep - 1].title}
            </h3>
            <p className="text-gray-600 mt-1">
              {steps[currentStep - 1].description}
            </p>
          </div>

          <CurrentStepComponent
            projectId={projectId}
            projectName={projectName}
            setupData={setupData}
            onComplete={(data) => handleStepComplete(currentStep, data)}
            onPrevious={currentStep > 1 ? handlePrevious : null}
          />
        </div>

        {/* Footer Navigation */}
        <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <div className="text-sm text-gray-500">
              Step {currentStep} of {steps.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataroomSetupWizard;
